export type DateGroupItemType = {
  month: string
  dates: { date: string; status: string }[]
}

export const groupDatesByMonth = (
  dates: { date: string; status: string }[]
): DateGroupItemType[] => {
  return dates.reduce((acc, date) => {
    const month = date.date.split('-')[1]

    if (!acc.find((a) => a.month === month)) {
      acc.push({
        month,
        dates: [],
      })
    }

    const target = acc.find((a) => a.month === month)
    if (target) {
      target.dates.push(date)
    }

    return acc
  }, [] as DateGroupItemType[])
}

export const daysBeforeDateInWeek = (date: string): number => {
  const day = new Date(date).getDay()
  return day === 0 ? 6 : day - 1
}

export const daysAfterDateInWeek = (date: string): number => {
  const day = new Date(date).getDay()
  return day === 0 ? 0 : 7 - day
}

const abs = (n: bigint) => (n < 0n ? -n : n)

export const msToHHmm = (
  ms: string
): {
  hours: number
  minutes: number
} => {
  const minutes = abs(BigInt(ms)) / 60000n
  const hours = minutes / 60n
  const minutesLeft = minutes % 60n

  return {
    hours: Number(hours),
    minutes: Number(minutesLeft),
  }
}

export const msToMMss = (
  ms: number
): {
  minutes: number
  seconds: number
} => {
  const seconds = Math.floor(Math.abs(ms) / 1000)
  const minutes = Math.floor(seconds / 60)
  const secondsLeft = seconds % 60

  return {
    minutes: minutes,
    seconds: secondsLeft,
  }
}

export const msToTimeText = (ms: number): string => {
  const seconds = Math.floor(Math.abs(ms) / 1000)
  const minutes = Math.floor(seconds / 60)
  const secondsLeft = seconds % 60

  let result = ''
  if (minutes > 0) {
    result += `${minutes}分`
  }
  if (secondsLeft > 0) {
    result += `${secondsLeft}秒`
  }
  if (result === '') result = '0秒' // Fallback

  return result
}

export const formatChatDate = (date: string): string => {
  const today = new Date()
  const chatDate = new Date(date)

  if (
    chatDate.getDate() === today.getDate() &&
    chatDate.getMonth() === today.getMonth() &&
    chatDate.getFullYear() === today.getFullYear()
  ) {
    return 'Today'
  }

  const yesterday = new Date(today)
  yesterday.setDate(today.getDate() - 1)

  if (
    chatDate.getDate() === yesterday.getDate() &&
    chatDate.getMonth() === yesterday.getMonth() &&
    chatDate.getFullYear() === yesterday.getFullYear()
  ) {
    return 'Yesterday'
  }

  const formatter = new Intl.DateTimeFormat('ja-JP', {
    month: '2-digit',
    day: '2-digit',
    weekday: 'short',
    timeZone: 'Asia/Tokyo',
  })
  return formatter.format(chatDate)
}

const dateFormater = new Intl.DateTimeFormat('ja-JP', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  timeZone: 'Asia/Tokyo',
})

export const formatDate = (date: string | Date): string => {
  date = typeof date == 'string' ? new Date(date) : date

  return dateFormater.format(date)
}

export const formatDateJP = (date: string | Date): string => {
  date = typeof date == 'string' ? new Date(date) : date

  return dateFormater
    .formatToParts(date)
    .map(({ type, value }) => {
      switch (type) {
        case 'year':
          return `${value}年`
        case 'month':
          return `${value}月`
        case 'day':
          return `${value}日`
        case 'literal':
          return ''
        default:
          return value
      }
    })
    .join('')
}

export const remainDaysInMonth = (date: string): number => {
  const today = new Date(date)
  const nextMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0)

  return nextMonth.getDate() - today.getDate()
}

export function iso8601DateString(date: Date) {
  const yyyy = '' + date.getFullYear()
  const mm = ('' + (date.getMonth() + 1)).padStart(2, '0')
  const dd = ('' + date.getDate()).padStart(2, '0')

  return `${yyyy}-${mm}-${dd}`
}

const fullDateFormater = new Intl.DateTimeFormat('ja-JP', {
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Tokyo',
})

export const formatDateJPFull = (date: string | Date): string => {
  date = typeof date == 'string' ? new Date(date) : date

  return fullDateFormater
    .formatToParts(date)
    .map(({ type, value }) => {
      switch (type) {
        case 'year':
          return `${value}年`
        case 'month':
          return `${value}月`
        case 'day':
          return `${value}日`
        case 'hour':
          return ` ${value}:`
        case 'literal':
          return ''
        default:
          return value
      }
    })
    .join('')
}

const noYearDateTimeFormatter = new Intl.DateTimeFormat('ja-JP', {
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  timeZone: 'Asia/Tokyo',
})

export const formatDateJPNoYear = (date: string | Date): string => {
  date = typeof date == 'string' ? new Date(date) : date

  return noYearDateTimeFormatter
    .formatToParts(date)
    .map(({ type, value }) => {
      switch (type) {
        case 'year':
          return `${value}年`
        case 'month':
          return `${value}月`
        case 'day':
          return `${value}日`
        case 'hour':
          return ` ${value}:`
        case 'literal':
          return ''
        default:
          return value
      }
    })
    .join('')
}

const simpleDateTimeFormater = new Intl.DateTimeFormat('ja-JP', {
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  timeZone: 'Asia/Tokyo',
})

export const formatDateTime = (date: string | Date): string => {
  date = typeof date == 'string' ? new Date(date) : date

  return simpleDateTimeFormater.format(date)
}

const simpleTimeFormater = new Intl.DateTimeFormat('ja-JP', {
  hour: 'numeric',
  minute: 'numeric',
  timeZone: 'Asia/Tokyo',
})

export const formatTime = (date: string | Date): string => {
  date = typeof date == 'string' ? new Date(date) : date

  return simpleTimeFormater.format(date)
}

export const calculateAge = ({
  birthdateYear,
  birthdateMonth,
  birthdateDay,
}: {
  birthdateYear: number
  birthdateMonth: number
  birthdateDay: number
}): number => {
  const today = new Date()
  const birthDate = new Date(birthdateYear, birthdateMonth - 1, birthdateDay)
  let age = today.getFullYear() - birthDate.getFullYear()
  const m = today.getMonth() - birthDate.getMonth()
  if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
    age--
  }
  return age
}

export const genGradeText = (grade: number): string => {
  if (grade < 0) return '年少以下'
  if (grade > 8) return '中学生以上'

  return (
    {
      0: '年少以下',
      1: '年中',
      2: '年長',
    }[grade] ?? `小学${grade - 2}年生`
  )
}

export const calculateGrade = ({
  birthdateYear,
  birthdateMonth,
  birthdateDay,
}: {
  birthdateYear: number
  birthdateMonth: number
  birthdateDay: number
}): number => {
  const MIN_SCHOOL_GRADE = 0
  const MAX_SCHOOL_GRADE = 9
  const userBirthDate = new Date(
    birthdateYear,
    birthdateMonth - 1,
    birthdateDay
  )
  const today = new Date()
  const currentYear = new Date(
    today.setMonth(today.getMonth() - 3)
  ).getFullYear()
  let birthYear = birthdateYear
  if (
    userBirthDate.getMonth() + 1 < 4 ||
    (userBirthDate.getMonth() + 1 === 4 && userBirthDate.getDate() < 2)
  ) {
    birthYear--
  }

  const nendoAge = currentYear - birthYear

  let result = 0

  switch (nendoAge) {
    case 0:
    case 1:
    case 2:
    case 3:
    case 4:
      result = 0
      break
    case 5:
      result = 1
      break
    case 6:
      result = 2
      break
    case 7:
      result = 3
      break
    case 8:
      result = 4
      break
    case 9:
      result = 5
      break
    case 10:
      result = 6
      break
    case 11:
      result = 7
      break
    case 12:
      result = 8
      break
    default:
      result = 9
      break
  }

  return Math.min(Math.max(result, MIN_SCHOOL_GRADE), MAX_SCHOOL_GRADE)
}

export const calculateNendoAge = ({
  birthdateYear,
  birthdateMonth,
  birthdateDay,
}: {
  birthdateYear: number
  birthdateMonth: number
  birthdateDay: number
}): number => {
  const userBirthDate = new Date(
    birthdateYear,
    birthdateMonth - 1,
    birthdateDay
  )
  const today = new Date()
  const currentYear = new Date(
    today.setMonth(today.getMonth() - 3)
  ).getFullYear()
  let birthYear = birthdateYear
  if (
    userBirthDate.getMonth() + 1 < 4 ||
    (userBirthDate.getMonth() + 1 === 4 && userBirthDate.getDate() < 2)
  ) {
    birthYear--
  }

  const nendoAge = currentYear - birthYear

  return nendoAge
}
